/**
 * THE ROOM'S STAGE (PRODUCT.md §2) — where this room is on Converse → Work → Plan, how it got there, and the owner's
 * "Keep this room conversational" hold (agents answer, nothing is built — e.g. a volatile market room). The stage is
 * EARNED by use (the first Do or Context build → Work; Plan → Plan) and never moves back; holding a room keeps what it
 * earned, so lifting the hold gives it all back.
 *
 *   <RoomStage value={{ stage, cap, canSet }} onSetCap={(cap) => …} />
 */
import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { usePlanWords } from '../utils/planWords.js';

export const STAGES = [
  { id: 'converse', label: 'Converse', hint: 'Talk it through — agents answer' },
  { id: 'work', label: 'Work', hint: 'Agents do work; the room keeps its context' },
  { id: 'plan', label: 'Plan', hint: 'Milestones, tasks, runs and verification' },
];

export default function RoomStage({ value = {}, onSetCap = null }) {
  const w = usePlanWords();
  const stage = STAGES.some((s) => s.id === value.stage) ? value.stage : 'converse';
  const held = value.cap === 'converse';
  const capWork = value.cap === 'work';
  const canSet = !!value.canSet && typeof onSetCap === 'function' && !capWork;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const at = STAGES.findIndex((s) => s.id === stage);
  const flip = async () => {
    setBusy(true); setError('');
    try { await onSetCap(held ? '' : 'converse'); } catch (e) { setError((e && e.message) || "Couldn't change it — try again."); }
    setBusy(false);
  };
  return (
    <div data-testid="room-stage" data-stage={stage} data-held={held ? 'true' : 'false'}>
      <ol className="mb-2 flex items-center gap-1" aria-label="Room stage">
        {STAGES.map((s, i) => (
          <li key={s.id} data-testid={`room-stage-${s.id}`} aria-current={i === at ? 'step' : undefined}
            title={s.hint}
            className={`flex-1 rounded-md px-2 py-1 text-center text-[11px] font-medium ${
              i === at ? 'bg-indigo-600 text-white' : i < at ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-50 text-gray-400'}`}>
            {s.label}
          </li>
        ))}
      </ol>
      <p className="mb-2 text-[11px] text-gray-500">
        {stage === 'plan' ? `${w.groups}, ${w.leavesLc}, runs and verification` : STAGES[at].hint}. A room moves forward as it's used — the first Do or Context build makes it Work; choosing
        Plan makes it Plan. It never moves back.
        {capWork ? ' This room never plans (its release is Context only).' : ''}
      </p>
      <label className={`flex items-start gap-2 text-[12px] ${canSet ? 'text-gray-800' : 'text-gray-400'}`}>
        <input type="checkbox" data-testid="room-stage-hold" checked={held} disabled={!canSet || busy} onChange={flip}
          className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-indigo-600" />
        <span>
          Keep this room conversational
          <span className="block text-[11px] text-gray-500">Agents answer; nothing is built (no Do, no Context build).
            What the room earned is kept for when you lift it.</span>
        </span>
        {busy ? <Loader2 className="mt-0.5 h-3.5 w-3.5 animate-spin text-gray-400" aria-hidden /> : null}
      </label>
      {error ? <div className="mt-1 text-[11px] text-rose-600">{error}</div> : null}
    </div>
  );
}
